import React, { useMemo, useState } from 'react';
import { formatCurrency, formatNumber } from '../../../../utils/formatters';
import {
  DIPENDENTI, MESI_DISPONIBILI, PREZZO_IN_VIGORE, SESSIONI, SOCIETA, STATO_SESSIONE,
  Sessione, StatoSessione, arrotonda2, etichettaMese, formatDataOra, meseDi, whInKwh,
} from './dati';
import { Campo, Kpi } from './componenti';

interface FormConferma {
  dipendenteId: string;
  energiaKwh: string;
  nota: string;
}

// Voce del registro di una sessione (nella pagina vera: SessioneRicaricaLog)
interface VoceRegistro {
  createdAt: string;
  utenteName: string;
  azione: string;
  dettaglio: string;
}

// Valori dei filtri sede e colonnina, ricavati dalle sessioni del mockup
const SEDI_SESSIONI = Array.from(new Set(SESSIONI.map((s) => s.colonnina.sede).filter((x): x is string => !!x))).sort();
const COLONNINE_SESSIONI = Array.from(
  new Map(SESSIONI.map((s): [number, string] => [s.colonnina.id, s.colonnina.nome])).entries(),
);

function nomeDipendente(d: Sessione['dipendente']): string {
  return d ? `${d.cognome} ${d.nome}` : 'non riconosciuto';
}

export default function VistaSessioni() {
  const [sessioni, setSessioni] = useState<Sessione[]>(SESSIONI);
  const [registro, setRegistro] = useState<Record<number, VoceRegistro[]>>({});
  const [fSocieta, setFSocieta] = useState('');
  const [fMese, setFMese] = useState(MESI_DISPONIBILI[0]);
  const [fSede, setFSede] = useState('');
  const [fColonnina, setFColonnina] = useState('');
  const [fStato, setFStato] = useState<'' | StatoSessione>('');
  const [fQ, setFQ] = useState('');
  const [aperta, setAperta] = useState<Sessione | null>(null);
  const [formConferma, setFormConferma] = useState<FormConferma>({ dipendenteId: '', energiaKwh: '', nota: '' });
  const [errore, setErrore] = useState('');

  const visibili = useMemo(() => {
    const nomeSocieta = SOCIETA.find((s) => s.id === Number(fSocieta))?.nome;
    const q = fQ.trim().toLowerCase();
    return sessioni.filter((s) => {
      // Una ricarica appartiene a due società: quella della colonnina e quella del dipendente
      if (nomeSocieta && s.colonnina.societaNome !== nomeSocieta && s.dipendente?.societaNome !== nomeSocieta) {
        return false;
      }
      if (fMese && meseDi(s) !== fMese) return false;
      if (fSede && s.colonnina.sede !== fSede) return false;
      if (fColonnina && s.colonnina.id !== Number(fColonnina)) return false;
      if (fStato && s.stato !== fStato) return false;
      const testo = `${s.colonnina.nome} ${s.dipendente?.cognome ?? ''} ${s.dipendente?.nome ?? ''} ${s.codiceBadge}`;
      return !q || testo.toLowerCase().includes(q);
    });
  }, [sessioni, fSocieta, fMese, fSede, fColonnina, fStato, fQ]);

  const concluse = visibili.filter((s) => s.stato === 'CONCLUSA');
  const energiaTotWh = concluse.reduce((t, s) => t + (s.energiaWh ?? 0), 0);
  const importoTot = concluse.reduce((t, s) => t + (s.importo ?? 0), 0);

  const apri = (s: Sessione) => {
    setErrore('');
    setFormConferma({
      dipendenteId: s.dipendente ? String(s.dipendente.id) : '',
      energiaKwh: s.energiaWh !== null ? String(whInKwh(s.energiaWh)) : '',
      nota: '',
    });
    setAperta(s);
  };

  const conferma = () => {
    if (!aperta) return;
    const dipendente = DIPENDENTI.find((d) => d.id === Number(formConferma.dipendenteId));
    const testoKwh = formConferma.energiaKwh.trim().replace(',', '.');
    const kwh = Number(testoKwh);
    if (!dipendente) { setErrore('Scegli il dipendente a cui intestare la ricarica'); return; }
    if (testoKwh === '' || !Number.isFinite(kwh) || kwh < 0) {
      setErrore('Energia non valida: indica i kWh erogati (0 se nessuno)');
      return;
    }
    if (!formConferma.nota.trim()) { setErrore('Scrivi il motivo della conferma: resta nel registro'); return; }
    const prezzo = PREZZO_IN_VIGORE[aperta.colonnina.societaNome] ?? 0;
    const energiaWh = Math.round(kwh * 1000);
    const confermata: Sessione = {
      ...aperta,
      dipendente: { id: dipendente.id, nome: dipendente.nome, cognome: dipendente.cognome, societaNome: 'Pianinvest' },
      energiaWh, prezzoKwh: prezzo, importo: arrotonda2(whInKwh(energiaWh) * prezzo),
      stato: 'CONCLUSA', motivoAnomalia: null, competenza: { anno: 2026, mese: 10 }, tardiva: false,
    };
    // Stesso formato di buildDiffLog: "Campo: 'prima' → 'dopo' | …"
    const modifiche: string[] = [];
    if (aperta.dipendente?.id !== dipendente.id) {
      modifiche.push(`Dipendente: '${nomeDipendente(aperta.dipendente)}' → '${nomeDipendente(confermata.dipendente)}'`);
    }
    if (aperta.energiaWh !== energiaWh) {
      modifiche.push(`Energia: '${formatNumber(whInKwh(aperta.energiaWh ?? 0))} kWh' → '${formatNumber(whInKwh(energiaWh))} kWh'`);
    }
    modifiche.push(`Motivo: '${formConferma.nota.trim()}'`);
    const voce: VoceRegistro = {
      createdAt: new Date().toISOString(), utenteName: 'Paolo', azione: 'Conferma della ricarica anomala',
      dettaglio: modifiche.join(' | '),
    };
    setSessioni(sessioni.map((s) => (s.id === aperta.id ? confermata : s)));
    setRegistro({ ...registro, [aperta.id]: [...(registro[aperta.id] ?? []), voce] });
    setAperta(null);
  };

  const vociRegistro = aperta ? registro[aperta.id] ?? [] : [];

  return (
    <div className="bop-ricarica__stack">
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2 className="bop-page-title">Sessioni di ricarica</h2>
          <p className="bop-page-subtitle">
            Ogni ricarica compare sia alla società della colonnina sia a quella del dipendente
          </p>
        </div>
      </div>

      <div className="bop-ricarica__kpi-grid">
        <Kpi label="Ricariche concluse" valore={String(concluse.length)} />
        <Kpi label="Energia" valore={`${formatNumber(whInKwh(energiaTotWh))} kWh`} />
        <Kpi label="Importo" valore={`${formatCurrency(importoTot)} €`} />
        <Kpi label="Anomale" valore={String(visibili.filter((s) => s.stato === 'ANOMALA').length)} />
      </div>

      <div className="bop-filter-bar">
        <select className="bop-select" value={fSocieta} onChange={(e) => setFSocieta(e.target.value)}>
          <option value="">Tutte le società</option>
          {SOCIETA.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
        </select>
        <select className="bop-select" value={fMese} onChange={(e) => setFMese(e.target.value)}>
          {MESI_DISPONIBILI.map((m) => (<option key={m} value={m}>{etichettaMese(m)}</option>))}
        </select>
        <select className="bop-select" value={fSede} onChange={(e) => setFSede(e.target.value)}>
          <option value="">Tutte le sedi</option>
          {SEDI_SESSIONI.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <select className="bop-select" value={fColonnina} onChange={(e) => setFColonnina(e.target.value)}>
          <option value="">Tutte le colonnine</option>
          {COLONNINE_SESSIONI.map(([id, nome]) => (<option key={id} value={id}>{nome}</option>))}
        </select>
        <select className="bop-select" value={fStato} onChange={(e) => setFStato(e.target.value as '' | StatoSessione)}>
          <option value="">Tutti gli stati</option>
          <option value="IN_CORSO">In corso</option>
          <option value="CONCLUSA">Concluse</option>
          <option value="ANOMALA">Anomale</option>
        </select>
        <input className="bop-input" placeholder="Cerca dipendente, colonnina o badge…" value={fQ}
          onChange={(e) => setFQ(e.target.value)} />
      </div>

      <div className="bop-card">
        <div className="bop-table-wrapper">
          {visibili.length === 0 ? (
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Nessuna ricarica</h3>
              <p className="bop-empty-state__text">Nessuna ricarica trovata con i filtri selezionati</p>
            </div>
          ) : (
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Inizio</th>
                  <th>Colonnina</th>
                  <th>Dipendente</th>
                  <th className="bop-text-right">kWh</th>
                  <th className="bop-text-right">Importo</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {visibili.map((s) => (
                  <tr key={s.id} onClick={() => apri(s)}>
                    <td>{formatDataOra(s.inizioAt)}</td>
                    <td>
                      {s.colonnina.nome}
                      <div className="bop-ricarica__secondaria">
                        {s.colonnina.societaNome}{s.colonnina.sede ? ` · ${s.colonnina.sede}` : ''}
                      </div>
                    </td>
                    <td>
                      {s.dipendente ? (
                        <>
                          {s.dipendente.cognome} {s.dipendente.nome}
                          <div className="bop-ricarica__secondaria">{s.dipendente.societaNome}</div>
                        </>
                      ) : (
                        <span className="bop-text-danger">Badge {s.codiceBadge}</span>
                      )}
                    </td>
                    <td className="bop-text-right">{s.energiaWh !== null ? formatNumber(whInKwh(s.energiaWh)) : '—'}</td>
                    <td className="bop-text-right">{s.importo !== null ? `${formatCurrency(s.importo)} €` : '—'}</td>
                    <td>
                      <span className="bop-ricarica__connettori">
                        <span className={`bop-badge ${STATO_SESSIONE[s.stato].badge}`}>{STATO_SESSIONE[s.stato].label}</span>
                        {s.tardiva && <span className="bop-badge bop-badge--warning">Tardiva</span>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {aperta && (
        <div className="bop-modal-overlay" onClick={() => setAperta(null)}>
          <div className="bop-modal bop-modal--lg" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Ricarica n. {aperta.transactionId}</h2>
              <button className="bop-modal__close" onClick={() => setAperta(null)} aria-label="Chiudi">✕</button>
            </div>
            <div className="bop-modal__body bop-ricarica__form">
              <div className="bop-ricarica__campi">
                <Campo label="Colonnina">{aperta.colonnina.nome} · presa {aperta.connettore}</Campo>
                <Campo label="Società della colonnina">
                  {aperta.colonnina.societaNome}{aperta.colonnina.sede ? ` · ${aperta.colonnina.sede}` : ''}
                </Campo>
                <Campo label="Badge"><span className="bop-text-mono">{aperta.codiceBadge}</span></Campo>
                <Campo label="Dipendente">
                  {aperta.dipendente
                    ? `${aperta.dipendente.cognome} ${aperta.dipendente.nome} (${aperta.dipendente.societaNome})`
                    : 'Non riconosciuto'}
                </Campo>
                <Campo label="Inizio">{formatDataOra(aperta.inizioAt)}</Campo>
                <Campo label="Fine">{aperta.fineAt ? formatDataOra(aperta.fineAt) : 'In corso'}</Campo>
                <Campo label="Energia">
                  {aperta.energiaWh !== null ? `${formatNumber(whInKwh(aperta.energiaWh))} kWh` : '—'}
                </Campo>
                <Campo label="Tariffa">
                  {aperta.prezzoKwh !== null ? `${formatCurrency(aperta.prezzoKwh)} €/kWh` : '—'}
                </Campo>
                <Campo label="Importo">{aperta.importo !== null ? `${formatCurrency(aperta.importo)} €` : '—'}</Campo>
                <Campo label="Mese di riaddebito">
                  {aperta.competenza ? etichettaMese(meseDi(aperta)) : 'Non ancora assegnato'}
                </Campo>
              </div>
              {aperta.tardiva && (
                <div className="bop-alert bop-alert--warning bop-mb-0">
                  Ricarica arrivata dopo la chiusura del mese in cui è finita: conteggiata nel primo mese aperto.
                </div>
              )}
              {aperta.stato === 'ANOMALA' && (
                <>
                  <div className="bop-alert bop-alert--danger bop-mb-0">{aperta.motivoAnomalia}</div>
                  <p className="bop-ricarica__nota">
                    Una ricarica anomala non entra nel riaddebito finché non viene confermata. Controlla i dati, correggili
                    se serve e scrivi il motivo: resta nel registro.
                  </p>
                  {errore && <div className="bop-alert bop-alert--danger bop-mb-0">{errore}</div>}
                  <div className="bop-form-row">
                    <div className="bop-field">
                      <label className="bop-field__label" htmlFor="conf-dip">
                        Dipendente <span className="bop-field__required">*</span>
                      </label>
                      <select id="conf-dip" className="bop-field__select" value={formConferma.dipendenteId}
                        onChange={(e) => setFormConferma({ ...formConferma, dipendenteId: e.target.value })}>
                        <option value="">Scegli…</option>
                        {DIPENDENTI.map((d) => (<option key={d.id} value={d.id}>{d.cognome} {d.nome}</option>))}
                      </select>
                    </div>
                    <div className="bop-field">
                      <label className="bop-field__label" htmlFor="conf-kwh">
                        Energia kWh <span className="bop-field__required">*</span>
                      </label>
                      <input id="conf-kwh" type="number" min="0" step="0.01" className="bop-field__input"
                        value={formConferma.energiaKwh}
                        onChange={(e) => setFormConferma({ ...formConferma, energiaKwh: e.target.value })} />
                    </div>
                  </div>
                  <div className="bop-field">
                    <label className="bop-field__label" htmlFor="conf-nota">
                      Motivo <span className="bop-field__required">*</span>
                    </label>
                    <textarea id="conf-nota" className="bop-field__textarea" rows={3}
                      placeholder="Es. badge personale di Rossi non ancora registrato, verificato con lui"
                      value={formConferma.nota}
                      onChange={(e) => setFormConferma({ ...formConferma, nota: e.target.value })} />
                  </div>
                </>
              )}
              {vociRegistro.length > 0 && (
                <>
                  <div className="bop-section-label">Registro</div>
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Quando</th>
                        <th>Utente</th>
                        <th>Azione</th>
                        <th>Dettaglio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vociRegistro.map((v) => (
                        <tr key={v.createdAt}>
                          <td>{formatDataOra(v.createdAt)}</td>
                          <td>{v.utenteName}</td>
                          <td>{v.azione}</td>
                          <td className="bop-text-muted">{v.dettaglio}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setAperta(null)}>Chiudi</button>
              {aperta.stato === 'ANOMALA' && (
                <button className="bop-btn bop-btn-primary" onClick={conferma}>Conferma ricarica</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
